/**
 * lessonsApi — the typed axios surface over the state-curriculum lesson backend
 * (routes/lessons.py, M3). Mirrors the module discipline of prepApi /
 * learningPathsApi / coppaApi: each endpoint is a named `async function` that
 * awaits the shared axios instance and returns `res.data`, so the URL shapes and
 * envelope reads live in ONE place both LessonView and ExerciseLayout import.
 *
 * AUTH: the standard bearer token carried by the shared axios interceptor (every
 * call here is the caller's OWN data — node-ownership enforced server-side).
 *
 * ERROR CONTRACT — deliberately NOT swallowed (callers branch on status):
 *   - `getNodeLesson` 404  → this node has no composed lesson → hide Learn
 *     entirely (practice is unaffected — no dead ends). See `isLessonUnavailable`.
 *   - `completeNodeLesson` 409 → the pinned `lesson_view` went stale (a v2
 *     publish landed mid-read, or the 24h window elapsed) → re-GET the lesson and
 *     re-render with a gentle "we refreshed the lesson" note, NOT an error tone.
 *     See `isLessonViewStale`.
 *   - `completeNodeLesson` a WRONG quick-check answer is NOT an error — it's an
 *     HTTP 200 with `passed:false` + score/total (encourage palette, retry).
 *   - `updateCurriculumJurisdiction` 403 `consent_required` is handled by the
 *     GLOBAL age/consent interceptor (raises the parent-invite overlay); the
 *     caller still gets the rejection so it can render its own calm inline note.
 *
 * `config` (optional trailing arg) forwards an AbortController `{ signal }` to
 * axios verbatim, so a node switch / unmount cancels an in-flight lesson GET —
 * the same cancel-on-node-switch contract the exercise question calls use.
 */
import axios from 'axios';
import { API_URL } from './config';

const LEARNING_PATHS_BASE = `${API_URL}/api/v1/learning-paths`;
const PROFILE_BASE = `${API_URL}/api/v1/profile`;

/**
 * The ordered curriculum-jurisdiction options for the "My state's standards"
 * picker — MIRRORS the backend allowlist (`app.lesson_constants
 * .CURRICULUM_JURISDICTIONS`, Sol-3). A value NOT in this closed set 422s on the
 * PATCH (fail-closed against a wrong-standard citation), so the picker must send
 * these verbatim. "Multi-State" (Common Core) leads; the 50 states follow
 * alphabetically. The UI adds a "Not set" option mapping to `null` (clears the
 * selection → unknown / Multi-State with no alignment claim) — `null` is a valid
 * column value but deliberately NOT a member here.
 */
export const CURRICULUM_JURISDICTIONS = [
  'Multi-State',
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

/**
 * GET /api/v1/learning-paths/{pathId}/nodes/{nodeId}/lesson — compose + serve a
 * node's lesson, minting a version-pinned view.
 *
 * Response envelope (routes/lessons.py `get_node_lesson`):
 *   { view_id: string,
 *     jurisdiction_applied: string,
 *     recommended_mode: 'explore' | 'learn' | 'practice',
 *     sections: [{ lc_identifier, version, title, body_md,
 *                  alignment: { standard_code, statement, attribution } | null,
 *                  quick_check: [{ question, options: string[] }] }],
 *     completion: { completed_at, quick_check_score } | null,
 *     xp_available: number }
 *
 * The quick-check ANSWER KEY never leaves the server (the GET strips it); the
 * completion POST grades server-side. A 404 means "no lesson for this node".
 * `config` forwards the AbortController `{ signal }`.
 */
export async function getNodeLesson(pathId, nodeId, config) {
  const res = await axios.get(
    `${LEARNING_PATHS_BASE}/${pathId}/nodes/${nodeId}/lesson`,
    config,
  );
  return res.data;
}

/**
 * POST /api/v1/learning-paths/{pathId}/nodes/{nodeId}/lesson/complete — grade the
 * quick-check against the PINNED view + award XP once.
 *
 * `body` is `{ view_id: string, answers: number[] }` — one chosen option index
 * per quick-check item, flattened in SERVED order (section order, then item
 * order within each section, exactly as the GET returned them).
 *
 * Response `{ passed, xp_earned, already_completed, score, total }`. A wrong
 * answer returns 200 `passed:false` (encourage palette, NOT an error). A 409
 * means the pinned view is stale → re-GET (see `isLessonViewStale`). `config`
 * forwards the AbortController `{ signal }`.
 */
export async function completeNodeLesson(pathId, nodeId, body, config) {
  const res = await axios.post(
    `${LEARNING_PATHS_BASE}/${pathId}/nodes/${nodeId}/lesson/complete`,
    body,
    config,
  );
  return res.data;
}

/**
 * PATCH /api/v1/profile/curriculum-jurisdiction — set or clear the student's
 * curriculum selection. `jurisdiction` is a `CURRICULUM_JURISDICTIONS` member OR
 * `null` (clear → unknown/Multi-State). Response `{ jurisdiction }`.
 *
 * The write carries the backend consent gate (Sol-3): an under-13 in limited
 * mode gets a 403 `consent_required`, which the GLOBAL interceptor turns into
 * the parent-invite overlay before rejecting here. An unknown value 422s. We do
 * NOT swallow either — the caller renders its own calm inline state.
 */
export async function updateCurriculumJurisdiction(jurisdiction) {
  const res = await axios.patch(`${PROFILE_BASE}/curriculum-jurisdiction`, {
    jurisdiction,
  });
  return res.data;
}

/**
 * True when an axios error means this node has NO lesson (the `getNodeLesson`
 * 404). The Learn surface branches on this to SELF-HIDE — practice is unaffected
 * (no dead ends), mirroring how the DSAT exam + explore surfaces hide on a 404.
 */
export function isLessonUnavailable(err) {
  return err?.response?.status === 404;
}

/**
 * True when a `completeNodeLesson` error means the pinned `lesson_view` went
 * stale (409). The caller re-GETs the lesson (fresh view_id) and re-renders with
 * a gentle "we refreshed the lesson" note — never an error tone.
 */
export function isLessonViewStale(err) {
  return err?.response?.status === 409;
}
